/**
 * API Authentication documentation page
 */

import {DocsContent} from '../../../components/docs/DocsContent';
import {CodeBlock} from '../../../components/docs/CodeBlock';
import {DocsActionButtons} from '../../../components/docs/DocsActionButtons';
import {Link} from 'react-router-dom';

export function APIAuthentication() {
  return (
    <DocsContent
      title="API Authentication"
      description="Learn how to authenticate with the VoxaPilot API."
    >
      <div className="space-y-8">
        <section>
          <h2 className="text-2xl font-bold text-textPrimary mb-4">API Keys</h2>
          <p className="text-textSecondary mb-4">
            VoxaPilot uses API keys for authentication. Each API key is associated with your account
            and has specific permissions.
          </p>

          <h3 className="text-xl font-semibold text-textPrimary mb-3 mt-6">Creating an API Key</h3>
          <ol className="list-decimal list-inside space-y-2 text-textSecondary mb-6">
            <li>Go to Settings → API Keys</li>
            <li>Click "Create API Key"</li>
            <li>Give it a name and select permissions</li>
            <li>Copy the key immediately (it won't be shown again)</li>
          </ol>

          <h3 className="text-xl font-semibold text-textPrimary mb-3 mt-6">Using an API Key</h3>
          <p className="text-textSecondary mb-4">
            Include your API key in the Authorization header of all requests:
          </p>

          <CodeBlock
            code={`// Using curl
curl -H "Authorization: Bearer YOUR_API_KEY" \\
     https://api.voxapilot.com/v1/meetings

// Using JavaScript fetch
const response = await fetch('https://api.voxapilot.com/v1/meetings', {
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json',
  },
});`}
            language="javascript"
          />
        </section>

        <section>
          <h2 className="text-2xl font-bold text-textPrimary mb-4">Permissions</h2>
          <p className="text-textSecondary mb-4">
            API keys can have different permission levels:
          </p>

          <ul className="list-disc list-inside space-y-2 text-textSecondary mb-6">
            <li>
              <strong>Read:</strong> View meetings, transcripts, and summaries
            </li>
            <li>
              <strong>Write:</strong> Create meetings and upload audio
            </li>
            <li>
              <strong>Admin:</strong> Full access including account management
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-textPrimary mb-4">Security Best Practices</h2>
          <ul className="list-disc list-inside space-y-2 text-textSecondary mb-6">
            <li>Never commit API keys to version control</li>
            <li>Use environment variables to store keys</li>
            <li>Rotate keys regularly</li>
            <li>Use the minimum required permissions</li>
            <li>Revoke unused keys immediately</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-textPrimary mb-4">Next Steps</h2>
          <ul className="list-disc list-inside space-y-2 text-textSecondary dark:text-[#858585]">
            <li>
              <Link to="/docs/api/endpoints" className="text-gray-900 dark:text-orange-500 underline hover:text-gray-700 dark:hover:text-orange-400">
                Explore API endpoints
              </Link>
            </li>
          </ul>
        </section>

        <DocsActionButtons pageTitle="API Authentication" />
      </div>
    </DocsContent>
  );
}

